/**
 *
 * ManageNisaParameters
 *
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, Spinner, SystemStyleObject, Text, useDisclosure } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ROUTES } from 'config/routes';
import { omit } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import CustomModal from 'app/components/CustomModal';
import InputNumeric from 'app/components/InputNumeric';
import Select, { IOption } from 'app/components/Select';
import { useSession } from 'hooks/useSession';
import { useCustomToast } from 'hooks/useToast';
import { getAllCompanies, IResponseCompanies } from 'services/http/company';
import { getHistoryYears, IMonthsOfTheYear } from 'services/http/flow';
import {
	createNisaParameters,
	editNisaParameters,
	getAllNisaParameters,
	IResponseNisaParameters,
} from 'services/http/nisaParameters';
import { DEPARTMENTS_OPTIONS } from 'utils/constants';
import { handleDisableSelect } from 'utils/handlePermissions';
import { unMaskThousands } from 'utils/Numbers';
import { parseErrors, ResponseErrors } from 'utils/parseErrors';
import { parsedOptionArray } from 'utils/parseOptionArray';

interface INisaParametersForm {
	id?: string;
	// fluxoLojaPassante: IMonthsOfTheYear;
	// fluxoLigacoes: IMonthsOfTheYear;
	comparecimentoLigacoes: IMonthsOfTheYear;
	// fluxoLojaVendedor: IMonthsOfTheYear;
	// ligacoesRecebidas: IMonthsOfTheYear;
	comparecimento: IMonthsOfTheYear;
	conversao: IMonthsOfTheYear;
	// totalSales: IMonthsOfTheYear;
	billings: IMonthsOfTheYear;
	averageTicket: IMonthsOfTheYear;
	complimentaryExpenses: IMonthsOfTheYear;
	percentageMb: IMonthsOfTheYear;
	financing: IMonthsOfTheYear;
	financingContracts: IMonthsOfTheYear;
	financingPenetration: IMonthsOfTheYear;
	financingEvasion: IMonthsOfTheYear;
	financingValuePenetration: IMonthsOfTheYear;
	carReturn: IMonthsOfTheYear;
	financingProfitability: IMonthsOfTheYear;
	tccb: IMonthsOfTheYear;
	tccbByContract: IMonthsOfTheYear;
	// tccbByCar: IMonthsOfTheYear;
	placement: IMonthsOfTheYear;
	placementByCar: IMonthsOfTheYear;
	capture: IMonthsOfTheYear;
	evaluation: IMonthsOfTheYear;
	txEvaluation: IMonthsOfTheYear;
	txBuy: IMonthsOfTheYear;
	txCapture: IMonthsOfTheYear;
	contributionMargin: IMonthsOfTheYear;
	totalGrossProfit: IMonthsOfTheYear;
	grossProfitwithoutRevenue: IMonthsOfTheYear;
	accessories: IMonthsOfTheYear;
	mediumAccessoryTicket: IMonthsOfTheYear;
	hgsi: IMonthsOfTheYear;
	nps: IMonthsOfTheYear;
	oficina: IMonthsOfTheYear;
	// leads: IMonthsOfTheYear;
	department: IOption;
	year: IOption;
	company: IOption;
}

const requiredOptionSchema = yup
	.object()
	.shape({
		label: yup.string(),
		value: yup.string().required('Este campo é obrigatório.'),
	})
	.required('Este campo é obrigatório.')
	.default(undefined)
	.nullable();

const monthsSchema = yup.object().shape({
	...Object.fromEntries(
		Array.from({ length: 12 }, (_, i) => [
			`month${i + 1}`,
			yup
				.string()
				.nullable()
				.default(null)
				.test('max-integer-digits', 'Máximo de 9 dígitos inteiros.', value => {
					if (!value || value === '0' || value === '0.00') return true;

					const integerPart = value.split('.')[0];

					const digitCount = integerPart.replace(/\D/g, '').length;

					return digitCount <= 9;
				}),
		]),
	),
});

const defaultMonths = {
	month1: 0,
	month2: 0,
	month3: 0,
	month4: 0,
	month5: 0,
	month6: 0,
	month7: 0,
	month8: 0,
	month9: 0,
	month10: 0,
	month11: 0,
	month12: 0,
};

const ManageNisaParameters = () => {
	const styles: Record<string, SystemStyleObject> = {
		container: {
			my: '9.5rem',
			justifyContent: 'center',
		},
		containerForm: {
			maxW: '48.75rem',
			w: 'full',
			bgColor: 'white',
			p: '48px 25px 48px 38px',
			gap: '2rem',
			flexDir: 'column',
		},
		scroll: {
			flexDirection: 'column',
			gap: '24px',
			height: '611px',
			overflow: 'auto',
			paddingRight: '11px',
			paddingLeft: '10px',
		},
		header: {
			justifyContent: 'space-between',
			w: 'full',
			alignItems: 'center',
			mb: {
				base: '1.5rem',
				md: '0.625rem',
			},
			gap: '1.5rem',
			flexWrap: 'wrap',
		},
		title: {
			fontSize: '1.875rem',
			fontWeight: '600',
		},
		selectsContainer: {
			gap: '24px',
			flexDirection: { base: 'column', lg: 'row' },
		},
		yearSelect: {
			width: { base: '100%', lg: '218px' },
		},
		grid: {
			gap: '1rem',
			gridTemplateColumns: {
				base: 'repeat(1, 1fr)',
				sm: 'repeat(2, 1fr)',
				md: 'repeat(4, 1fr)',
				lg: 'repeat(6, 1fr)',
			},
		},
		boxGrid: {
			mb: '1rem',
		},
		labelParameters: {
			color: 'blue.500',
			fontWeight: '700',
			mb: '10px',
		},
		buttonContainer: {
			gap: '1.875rem',
			justifyContent: 'flex-end',
			flexDir: {
				base: 'column-reverse',
				md: 'row',
			},
		},
		button: {
			w: {
				md: '9.25rem',
			},
			h: '4rem',
			fontSize: '1.375rem',
		},
		customLabelStyle: {
			fontSize: '1rem',
		},
	};

	const { addToast } = useCustomToast();
	const navigate = useNavigate();
	const { isOpen: isOpenDirty, onOpen: onOpenDirty, onClose: onCloseDirty } = useDisclosure();
	const { isOpen: isOpenCancel, onOpen: onOpenCancel, onClose: onCloseCancel } = useDisclosure();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isEditing, setIsEditing] = useState(false);
	const hasFetchedData = useRef(false);
	const [defaultFormValues, setDefaultFormValues] = useState<INisaParametersForm>();

	const {
		session: { user },
	} = useSession();

	const schema = yup.object().shape({
		department: requiredOptionSchema,
		year: requiredOptionSchema,
		company: requiredOptionSchema,
		// fluxoLojaPassante: monthsSchema,
		// fluxoLigacoes: monthsSchema,
		comparecimentoLigacoes: monthsSchema,
		// fluxoLojaVendedor: monthsSchema,
		// ligacoesRecebidas: monthsSchema,
		comparecimento: monthsSchema,
		conversao: monthsSchema,
		// totalSales: monthsSchema,
		billings: monthsSchema,
		averageTicket: monthsSchema,
		complimentaryExpenses: monthsSchema,
		percentageMb: monthsSchema,
		financing: monthsSchema,
		financingContracts: monthsSchema,
		financingPenetration: monthsSchema,
		financingEvasion: monthsSchema,
		financingValuePenetration: monthsSchema,
		carReturn: monthsSchema,
		financingProfitability: monthsSchema,

		tccb: monthsSchema,
		tccbByContract: monthsSchema,
		// tccbByCar: monthsSchema,
		placement: monthsSchema,
		placementByCar: monthsSchema,
		capture: monthsSchema,
		evaluation: monthsSchema,
		txEvaluation: monthsSchema,
		txBuy: monthsSchema,
		txCapture: monthsSchema,
		contributionMargin: monthsSchema,
		totalGrossProfit: monthsSchema,
		grossProfitwithoutRevenue: monthsSchema,
		accessories: monthsSchema,
		mediumAccessoryTicket: monthsSchema,
		hgsi: monthsSchema,
		nps: monthsSchema,
		oficina: monthsSchema,
		// leads: monthsSchema,
	});

	const {
		handleSubmit,
		control,
		watch,
		reset,
		getValues,
		setValue,
		formState: { errors, dirtyFields },
	} = useForm<INisaParametersForm>({
		resolver: yupResolver(schema),
	});

	const formValues = watch();

	const isSelectDisabled = handleDisableSelect(user);

	const isFieldsDisabled = useMemo(() => {
		return !formValues?.company?.value || !formValues?.department?.value || !formValues?.year?.value;
	}, [formValues?.company?.value, formValues?.department?.value, formValues?.year?.value]);

	const { data: companies } = useQuery<IResponseCompanies, AxiosError<ResponseErrors>, IResponseCompanies>(
		['companies'],
		() => getAllCompanies(),
		{
			onSuccess: data => {
				const defaultCompany = data?.companiesObjects?.find(item => String(item?.id) === String(user.company[0]));

				setValue('company', { label: defaultCompany?.company || '', value: defaultCompany?.id });
			},
			onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
		},
	);

	const parsedCompanies: IOption[] = useMemo(() => {
		return (
			companies?.companiesObjects?.map(item => {
				return {
					value: item.id!,
					label: item.company,
				};
			}) || []
		);
	}, [companies?.companiesObjects]);

	const { data: years } = useQuery<number[], AxiosError<ResponseErrors>, number[]>(
		['historyYears'],
		() => getHistoryYears(),
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
		},
	);

	const {
		data: nisaParameters,
		isLoading: isLoadingNisaParameters,
		refetch: refetchNisaParameters,
	} = useQuery<IResponseNisaParameters[], AxiosError<ResponseErrors>, IResponseNisaParameters[]>(
		['nisaParameters', formValues?.company?.value, formValues?.department?.value, formValues?.year?.value],
		() =>
			getAllNisaParameters({
				...getValues,
				company_id: String(formValues?.company?.value || ''),
				department: String(formValues?.department?.value || ''),
				year: Number(formValues?.year?.value || 0),
			}),
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
			enabled: !isFieldsDisabled,
		},
	);

	const { mutate: createNisaParametersMutate, isLoading: isLoadingCreate } = useMutation<
		void,
		AxiosError<ResponseErrors>,
		IResponseNisaParameters
	>(data => createNisaParameters(data), {
		onSuccess: onOpen,
		onError: error => addToast({ type: 'error', title: 'Erro!', description: parseErrors(error?.response?.data) }),
	});

	const { mutate: editNisaParametersMutate, isLoading: isLoadingEdit } = useMutation<
		IResponseNisaParameters,
		AxiosError<ResponseErrors>,
		IResponseNisaParameters
	>(data => editNisaParameters(data, nisaParameters?.[0]?.id!), {
		onSuccess: onOpen,
		onError: error => addToast({ type: 'error', description: parseErrors(error?.response?.data) }),
	});

	const [pendingFilterChange, setPendingFilterChange] = useState<{
		field: 'company' | 'department' | 'year';
		value: IOption | null;
	} | null>(null);

	const handleSelectChange = (
		field: 'company' | 'department' | 'year',
		newValue: IOption | null,
		onChange: (value: IOption | null) => void,
	) => {
		const hasDirtyFields = Object.keys(omit(dirtyFields, ['company', 'department', 'year'])).length > 0;

		if (hasDirtyFields) {
			setPendingFilterChange({ field, value: newValue });
			onOpenDirty();
			return;
		}

		onChange(newValue);
	};

	const confirmChange = () => {
		if (pendingFilterChange) {
			setValue(pendingFilterChange.field, pendingFilterChange?.value as IOption);

			refetchNisaParameters();
		}
		onCloseDirty();
		setPendingFilterChange(null);
	};

	const cancelChange = () => {
		onCloseDirty();
		setPendingFilterChange(null);
	};

	const onCloseSuccessModal = () => {
		onClose();
		navigate(ROUTES.home);
	};

	const onSubmit = (values: INisaParametersForm) => {
		const payload: IResponseNisaParameters = {
			// fluxoLojaPassante: values?.fluxoLojaPassante,
			// fluxoLigacoes: values?.fluxoLigacoes,
			comparecimentoLigacoes: values?.comparecimentoLigacoes,
			// fluxoLojaVendedor: values?.fluxoLojaVendedor,
			// ligacoesRecebidas: values?.ligacoesRecebidas,
			comparecimento: values?.comparecimento,
			conversao: values?.conversao,
			// totalSales: values?.totalSales,
			billings: values?.billings,
			averageTicket: values?.averageTicket,
			complimentaryExpenses: values?.complimentaryExpenses,
			percentageMb: values?.percentageMb,
			financing: values?.financing,
			financingContracts: values?.financingContracts,
			financingPenetration: values?.financingPenetration,
			financingEvasion: values?.financingEvasion,
			financingValuePenetration: values?.financingValuePenetration,
			carReturn: values?.carReturn,
			financingProfitability: values?.financingProfitability,

			tccb: values?.tccb,
			tccbByContract: values?.tccbByContract,
			// tccbByCar: values?.tccbByCar,
			placement: values?.placement,
			placementByCar: values?.placementByCar,
			capture: values?.capture,
			evaluation: values?.evaluation,
			txEvaluation: values?.txEvaluation,
			txBuy: values?.txBuy,
			txCapture: values?.txCapture,
			contributionMargin: values?.contributionMargin,
			totalGrossProfit: values?.totalGrossProfit,
			grossProfitwithoutRevenue: values?.grossProfitwithoutRevenue,
			accessories: values?.accessories,
			mediumAccessoryTicket: values?.mediumAccessoryTicket,
			hgsi: values?.hgsi,
			nps: values?.nps,
			oficina: values?.oficina,
			// leads: values?.leads,
			department: String(values?.department?.value),
			company_id: String(values?.company?.value),
			year: Number(values?.year?.value),
		};

		isEditing ? editNisaParametersMutate(payload) : createNisaParametersMutate(payload);
	};

	useEffect(() => {
		if (nisaParameters?.length) {
			const form = watch();

			setIsEditing(!!nisaParameters[0]?.id);

			setDefaultFormValues({
				// fluxoLojaPassante: nisaParameters[0]?.fluxoLojaPassante,
				// fluxoLigacoes: nisaParameters[0]?.fluxoLigacoes,
				comparecimentoLigacoes: nisaParameters[0]?.comparecimentoLigacoes,
				// fluxoLojaVendedor: nisaParameters[0]?.fluxoLojaVendedor,
				// ligacoesRecebidas: nisaParameters[0]?.ligacoesRecebidas,
				comparecimento: nisaParameters[0]?.comparecimento,
				conversao: nisaParameters[0]?.conversao,
				// totalSales: nisaParameters[0]?.totalSales,
				billings: nisaParameters[0]?.billings,
				averageTicket: nisaParameters[0]?.averageTicket,
				complimentaryExpenses: nisaParameters[0]?.complimentaryExpenses,
				percentageMb: nisaParameters[0]?.percentageMb,
				financing: nisaParameters[0]?.financing,
				financingContracts: nisaParameters[0]?.financingContracts,
				financingPenetration: nisaParameters[0]?.financingPenetration,
				financingEvasion: nisaParameters[0]?.financingEvasion,
				financingValuePenetration: nisaParameters[0]?.financingValuePenetration,
				carReturn: nisaParameters[0]?.carReturn,
				financingProfitability: nisaParameters[0]?.financingProfitability,

				tccb: nisaParameters[0]?.tccb,
				tccbByContract: nisaParameters[0]?.tccbByContract,
				// tccbByCar: nisaParameters[0]?.tccbByCar,
				placement: nisaParameters[0]?.placement,
				placementByCar: nisaParameters[0]?.placementByCar,
				capture: nisaParameters[0]?.capture,
				evaluation: nisaParameters[0]?.evaluation,
				txEvaluation: nisaParameters[0]?.txEvaluation,
				txBuy: nisaParameters[0]?.txBuy,
				txCapture: nisaParameters[0]?.txCapture,
				contributionMargin: nisaParameters[0]?.contributionMargin,
				totalGrossProfit: nisaParameters[0]?.totalGrossProfit,
				grossProfitwithoutRevenue: nisaParameters[0]?.grossProfitwithoutRevenue,
				accessories: nisaParameters[0]?.accessories,
				mediumAccessoryTicket: nisaParameters[0]?.mediumAccessoryTicket,
				hgsi: nisaParameters[0]?.hgsi,
				nps: nisaParameters[0]?.nps,
				oficina: nisaParameters[0]?.oficina,
				// leads: nisaParameters[0]?.leads,
				id: nisaParameters[0]?.id,
				company: form?.company,
				department: form?.department,
				year: form?.year,
			});

			reset({
				// fluxoLojaPassante: nisaParameters[0]?.fluxoLojaPassante,
				// fluxoLigacoes: nisaParameters[0]?.fluxoLigacoes,
				comparecimentoLigacoes: nisaParameters[0]?.comparecimentoLigacoes,
				// fluxoLojaVendedor: nisaParameters[0]?.fluxoLojaVendedor,
				// ligacoesRecebidas: nisaParameters[0]?.ligacoesRecebidas,
				comparecimento: nisaParameters[0]?.comparecimento,
				conversao: nisaParameters[0]?.conversao,
				// totalSales: nisaParameters[0]?.totalSales,
				billings: nisaParameters[0]?.billings,
				averageTicket: nisaParameters[0]?.averageTicket,
				complimentaryExpenses: nisaParameters[0]?.complimentaryExpenses,
				percentageMb: nisaParameters[0]?.percentageMb,
				financing: nisaParameters[0]?.financing,
				financingContracts: nisaParameters[0]?.financingContracts,
				financingPenetration: nisaParameters[0]?.financingPenetration,
				financingEvasion: nisaParameters[0]?.financingEvasion,
				financingValuePenetration: nisaParameters[0]?.financingValuePenetration,
				carReturn: nisaParameters[0]?.carReturn,
				financingProfitability: nisaParameters[0]?.financingProfitability,

				// tccb: nisaParameters[0]?.tccb,
				tccbByContract: nisaParameters[0]?.tccbByContract,
				// tccbByCar: nisaParameters[0]?.tccbByCar,
				placement: nisaParameters[0]?.placement,
				placementByCar: nisaParameters[0]?.placementByCar,
				capture: nisaParameters[0]?.capture,
				evaluation: nisaParameters[0]?.evaluation,
				txEvaluation: nisaParameters[0]?.txEvaluation,
				txBuy: nisaParameters[0]?.txBuy,
				txCapture: nisaParameters[0]?.txCapture,
				contributionMargin: nisaParameters[0]?.contributionMargin,
				totalGrossProfit: nisaParameters[0]?.totalGrossProfit,
				grossProfitwithoutRevenue: nisaParameters[0]?.grossProfitwithoutRevenue,
				accessories: nisaParameters[0]?.accessories,
				mediumAccessoryTicket: nisaParameters[0]?.mediumAccessoryTicket,
				hgsi: nisaParameters[0]?.hgsi,
				nps: nisaParameters[0]?.nps,
				oficina: nisaParameters[0]?.oficina,
				// leads: nisaParameters[0]?.leads,
				id: nisaParameters[0]?.id,
				company: form?.company,
				department: form?.department,
				year: form?.year,
			});
		} else {
			setIsEditing(false);
			setDefaultFormValues(undefined);
		}
	}, [watch, nisaParameters, isEditing, reset]);

	useEffect(() => {
		if (isEditing && defaultFormValues?.id && !hasFetchedData?.current) {
			Object.entries(defaultFormValues).forEach(([key, value]) => {
				setValue(key as keyof INisaParametersForm, value);
			});
			hasFetchedData.current = true;
		} else if (!isEditing) {
			const resetFields = [
				// 'fluxoLojaPassante',
				// 'fluxoLigacoes',
				'comparecimentoLigacoes',
				// 'fluxoLojaVendedor',
				// 'ligacoesRecebidas',
				'comparecimento',
				'conversao',
				// 'totalSales',
				'billings',
				'averageTicket',
				'complimentaryExpenses',
				'percentageMb',
				'financing',
				'financingContracts',
				'financingPenetration',
				'financingEvasion',
				'financingValuePenetration',
				'carReturn',
				'financingProfitability',

				'tccb',
				'tccbByContract',
				// 'tccbByCar',
				'placement',
				'placementByCar',
				'capture',
				'evaluation',
				'txEvaluation',
				'txBuy',
				'txCapture',
				'contributionMargin',
				'totalGrossProfit',
				'grossProfitwithoutRevenue',
				'accessories',
				'mediumAccessoryTicket',
				'hgsi',
				'nps',
				// 'leads',
				'oficina',
			];

			resetFields.forEach(field => {
				setValue(field as keyof INisaParametersForm, defaultMonths);
			});
		}
	}, [defaultFormValues, isEditing, setValue]);

	return (
		<>
			<Flex sx={styles.container}>
				<Flex sx={styles?.containerForm} as="form" noValidate onSubmit={handleSubmit(onSubmit)}>
					<Flex sx={styles.header}>
						<Text sx={styles.title} data-testid="text--title">
							{'Gestão do parâmetro Nisa '}
						</Text>
					</Flex>
					<Flex sx={styles?.scroll}>
						<Controller
							name="company"
							control={control}
							render={({ field: { value, onChange } }) => (
								<Select
									name="company"
									label="Unidade"
									placeholder="Unidade"
									onChange={value => {
										handleSelectChange('company', value as IOption, onChange);
									}}
									value={value}
									options={parsedCompanies}
									variant="small"
									formLabelVariant="small"
									errorMessageVariant="small"
									dataTestId="select--company"
									errorMessage={errors.company?.message}
									isDisabled={isSelectDisabled}
								/>
							)}
						/>
						<Flex sx={styles?.selectsContainer}>
							<Controller
								name="department"
								control={control}
								render={({ field: { value, onChange } }) => (
									<Select
										name="department"
										label="Departamento"
										placeholder="Departamento"
										onChange={e => handleSelectChange('department', e as IOption, onChange)}
										value={value}
										options={DEPARTMENTS_OPTIONS}
										variant="small"
										formLabelVariant="small"
										errorMessageVariant="small"
										dataTestId="select--department"
										errorMessage={errors.department?.message}
									/>
								)}
							/>
							<Box sx={styles?.yearSelect}>
								<Controller
									name="year"
									control={control}
									render={({ field: { value, onChange } }) => (
										<Select
											name="year"
											label="Ano"
											placeholder="Ano"
											onChange={e => handleSelectChange('year', e as IOption, onChange)}
											value={value}
											options={parsedOptionArray(years)}
											variant="small"
											formLabelVariant="small"
											errorMessageVariant="small"
											dataTestId="select--year"
											errorMessage={errors.year?.message}
										/>
									)}
								/>
							</Box>
						</Flex>

						{isLoadingNisaParameters && !isFieldsDisabled ? (
							<Spinner />
						) : (
							<>
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Fluxo de Loja (Passantes)</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`fluxoLojaPassante-month${i + 1}`}>
												<Controller
													name={`fluxoLojaPassante.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`fluxoLojaPassante.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--fluxoLojaPassante-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Fluxo de Ligaçoes Recebidas</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`fluxoLigacoes-month${i + 1}`}>
												<Controller
													name={`fluxoLigacoes.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`fluxoLigacoes.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--fluxoLigacoes-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Comparecimento Ligações</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`comparecimentoLigacoes-month${i + 1}`}>
												<Controller
													name={`comparecimentoLigacoes.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`comparecimentoLigacoes.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--comparecimentoLigacoes-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Fluxo Loja Vendedor</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`fluxoLojaVendedor-month${i + 1}`}>
												<Controller
													name={`fluxoLojaVendedor.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`fluxoLojaVendedor.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--fluxoLojaVendedor-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Ligações Recebidas</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`ligacoesRecebidas-month${i + 1}`}>
												<Controller
													name={`ligacoesRecebidas.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`ligacoesRecebidas.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--ligacoesRecebidas-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Comparecimento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`comparecimento-month${i + 1}`}>
												<Controller
													name={`comparecimento.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`comparecimento.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--comparecimento-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Conversão</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`conversao-month${i + 1}`}>
												<Controller
													name={`conversao.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`conversao.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--conversao-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>NPS</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`nps-month${i + 1}`}>
												<Controller
													name={`nps.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`nps.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--nps-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Atendimento Oficina</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`oficina-month${i + 1}`}>
												<Controller
													name={`oficina.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`oficina.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--oficina-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Leads</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`leads-month${i + 1}`}>
												<Controller
													name={`leads.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`leads.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--leads-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Vendas Total</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`totalSales-month${i + 1}`}>
												<Controller
													name={`totalSales.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`totalSales.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--totalSales-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Valor de Venda Mensal</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`billings-month${i + 1}`}>
												<Controller
													name={`billings.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`billings.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--billings-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Ticket Médio</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`averageTicket-month${i + 1}`}>
												<Controller
													name={`averageTicket.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`averageTicket.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--averageTicket-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Despesas com Cortesias</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`complimentaryExpenses-month${i + 1}`}>
												<Controller
													name={`complimentaryExpenses.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`complimentaryExpenses.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--complimentaryExpenses-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>% M.B</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`percentageMb-month${i + 1}`}>
												<Controller
													name={`percentageMb.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`percentageMb.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--percentageMb-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Financiamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financing-month${i + 1}`}>
												<Controller
													name={`financing.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financing.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financing-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Contratos Financiamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financingContracts-month${i + 1}`}>
												<Controller
													name={`financingContracts.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financingContracts.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financingContracts-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Penetracion Financiamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financingPenetration-month${i + 1}`}>
												<Controller
													name={`financingPenetration.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financingPenetration.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financingPenetration-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Financiamento fora</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financingEvasion-month${i + 1}`}>
												<Controller
													name={`financingEvasion.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financingEvasion.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financingEvasion-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Penetration Valor Financiado</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financingValuePenetration-month${i + 1}`}>
												<Controller
													name={`financingValuePenetration.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financingValuePenetration.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financingValuePenetration-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Retorno por Carro</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`carReturn-month${i + 1}`}>
												<Controller
													name={`carReturn.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`carReturn.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--carReturn-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Rentabilidade Financiamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`financingProfitability-month${i + 1}`}>
												<Controller
													name={`financingProfitability.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`financingProfitability.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--financingProfitability-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>TCCB</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`tccb-month${i + 1}`}>
												<Controller
													name={`tccb.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`tccb.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--tccb-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>TCCB por Contrato</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`tccbByContract-month${i + 1}`}>
												<Controller
													name={`tccbByContract.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`tccbByContract.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--tccbByContract-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								{/* <Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>TCCB por Carro</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`tccbByCar-month${i + 1}`}>
												<Controller
													name={`tccbByCar.month${i + 1}`}
													control={control}
													render={({ field }) => (
														<InputNumeric
															name={`tccbByCar.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--tccbByCar-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box> */}
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Emplacamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`placement-month${i + 1}`}>
												<Controller
													name={`placement.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`placement.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--placement-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Emplacamento por carro</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`placementByCar-month${i + 1}`}>
												<Controller
													name={`placementByCar.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`placementByCar.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--placementByCar-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Captação</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`capture-month${i + 1}`}>
												<Controller
													name={`capture.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`capture.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--capture-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Avaliação</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`evaluation-month${i + 1}`}>
												<Controller
													name={`evaluation.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`evaluation.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--evaluation-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															decimalScale={0}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Taxa avaliação</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`txEvaluation-month${i + 1}`}>
												<Controller
													name={`txEvaluation.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`txEvaluation.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--txEvaluation-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Taxa compra</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`txBuy-month${i + 1}`}>
												<Controller
													name={`txBuy.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`txBuy.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--txBuy-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Taxa captura</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`txCapture-month${i + 1}`}>
												<Controller
													name={`txCapture.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`txCapture.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--txCapture-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Margem de contribuição</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`contributionMargin-month${i + 1}`}>
												<Controller
													name={`contributionMargin.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`contributionMargin.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--contributionMargin-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Lucro Bruto Total</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`totalGrossProfit-month${i + 1}`}>
												<Controller
													name={`totalGrossProfit.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`totalGrossProfit.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--totalGrossProfit-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>(%) Lucro Bruto s/Faturamento</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`grossProfitwithoutRevenue-month${i + 1}`}>
												<Controller
													name={`grossProfitwithoutRevenue.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`grossProfitwithoutRevenue.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--grossProfitwithoutRevenue-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Acessórios</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`accessories-month${i + 1}`}>
												<Controller
													name={`accessories.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`accessories.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--accessories-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>Ticket Médio Acessorios</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`mediumAccessoryTicket-month${i + 1}`}>
												<Controller
													name={`mediumAccessoryTicket.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`mediumAccessoryTicket.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--mediumAccessoryTicket-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
								<Box sx={styles?.boxGrid}>
									<Text sx={styles?.labelParameters}>HGSI</Text>
									<Grid sx={styles.grid}>
										{Array.from({ length: 12 }, (_, i) => (
											<GridItem key={`hgsi-month${i + 1}`}>
												<Controller
													name={`hgsi.month${i + 1}`}
													control={control}
													render={({ field, fieldState }) => (
														<InputNumeric
															name={`hgsi.month${i + 1}`}
															label={`Mês ${i + 1}`}
															placeholder={'000'}
															data-testid={`input--hgsi-month${i + 1}`}
															variant="small"
															formLabelVariant="small"
															errorMessageVariant="small"
															value={field?.value}
															onChange={e => field?.onChange(unMaskThousands(e?.target?.value))}
															isDisabled={isFieldsDisabled}
															suffix="%"
															style={{ fontSize: '12px', padding: '0', height: '1rem' }}
															customLabelStyle={styles.customLabelStyle}
															errorMessage={fieldState.error?.message}
														/>
													)}
												/>
											</GridItem>
										))}
									</Grid>
								</Box>
							</>
						)}
					</Flex>
					<Flex sx={styles.buttonContainer}>
						<Button
							sx={styles.button}
							variant="outlined"
							onClick={!formValues ? () => navigate(ROUTES.home) : onOpenCancel}
							data-testid="button--back"
						>
							Voltar
						</Button>
						<Button
							sx={styles.button}
							type="submit"
							data-testid="button--submit"
							isLoading={isLoadingCreate || isLoadingEdit}
							isDisabled={!formValues?.company?.value || !formValues?.department?.value || !formValues?.year?.value}
						>
							{'Salvar'}
						</Button>
					</Flex>
				</Flex>
			</Flex>
			<CustomModal
				title="Atenção!"
				body="Existem campos não salvos no formulário. Caso prossiga esses dados serão perdidos. Deseja refazer a pesquisa?"
				isOpen={isOpenDirty}
				onClose={onCloseDirty}
				actions={[
					{
						datatestid: 'button--cancel',
						label: 'Não',
						type: 'outlined',
						onClick: cancelChange,
					},
					{
						datatestid: 'button--confirm',
						label: 'Sim',
						type: 'solid',
						onClick: confirmChange,
					},
				]}
			/>

			<CustomModal
				title=""
				body="Existem campos não salvos no formulário. Caso prossiga esses dados serão perdidos. Deseja cancelar?"
				isOpen={isOpenCancel}
				onClose={onCloseCancel}
				actions={[
					{
						datatestid: 'button--cancel',
						label: 'Não',
						type: 'outlined',
						onClick: onCloseCancel,
					},
					{
						datatestid: 'button--confirm',
						label: 'Sim',
						type: 'solid',
						onClick: () => navigate(ROUTES.home),
					},
				]}
			/>
			<CustomModal
				title="Sucesso!"
				body="Os dados foram cadastrados/editados com sucesso!"
				isOpen={isOpen}
				onClose={onCloseSuccessModal}
				actions={[
					{
						datatestid: 'button--confirm',
						label: 'Ir para página inicial',
						type: 'solid',
						onClick: () => navigate(ROUTES.home),
					},
				]}
			/>
		</>
	);
};

export default ManageNisaParameters;
